import { Injectable } from '@angular/core';
import { Observable, forkJoin, of } from 'rxjs';
import { GroupLookup, Lookup } from 'src/app/models/lookup.model';
import { SampleTasks } from 'src/app/models/sampleTasks.model';
import { Sample } from 'src/app/models/sample.model';
import { SampleTaskService } from 'src/app/services/sample-task.service';
import { TaskService } from 'src/app/services/task.service';
import { MatDialog } from '@angular/material/dialog';
import { ITaskDialogData, TaskDialogComponent } from '../task-dialog/task-dialog.component';

type FilteredTaskGroup = GroupLookup & { id: number; name: string };

@Injectable()

export class SampleTaskManagerService {
  
  constructor(
    private sampleTaskService: SampleTaskService,
    private taskService: TaskService,
    private dialog: MatDialog
  ) { }

  /**
   * Load sample tasks for a specific sample
   */
  loadSampleTasks(sampleId: string | null): Observable<SampleTasks[]> {
    if (!sampleId) {
      return of([]);
    }
    return this.sampleTaskService.getSampleTasks(sampleId);
  }

  /**
   * Load all available task groups
   */
  loadTasks(): Observable<GroupLookup[]> {
    return this.taskService.getGroupLookups();
  }

  /**
   * Initialize task data for editing
   */
  initTaskData(
    entity: Sample,
    tasks: (GroupLookup & { id: number; name: string })[]
  ): {
    filteredTasks: FilteredTaskGroup[][];
    taskSearch: string[];
    previousTaskValues: Map<SampleTasks, string>;
  } {
    const filteredTasks: FilteredTaskGroup[][] = [];
    const taskSearch: string[] = [];
    const previousTaskValues = new Map<SampleTasks, string>();

    (entity.sampleTasks || []).forEach((task, index) => {
      filteredTasks[index] = [...tasks];
      taskSearch[index] = '';
      if (task.taskId) {
        previousTaskValues.set(task, task.taskId);
      }
    });

    return { filteredTasks, taskSearch, previousTaskValues };
  }

  /**
   * Check if a task ID is already selected in other rows
   */
  isTaskSelected(taskId: Lookup['id'] | null, currentRow: SampleTasks, allTasks: SampleTasks[]): boolean {
    if (!taskId) return false;

    return allTasks
      ?.filter(row => row !== currentRow)
      ?.some(row => row.taskId === taskId);
  }

  /**
   * Handle task selection from dropdown
   */
  onTaskSelected(
    taskId: string,
    element: SampleTasks,
    tasks: (GroupLookup & { id: number; name: string })[]
  ): void {
    if (!taskId) return;
    for (const group of tasks) {
      const selectedTask = group.items?.find(item => item.id === taskId);
      if (selectedTask) {
        element.taskId = taskId;
        element.taskName = selectedTask.name;
        element.taskGroupName = group.groupName;
        break;
      }
    }
  }

  /**
   * Add a new task row
   */
  addTaskRow(entity: Sample): SampleTasks {
    if (!entity.sampleTasks) {
      entity.sampleTasks = [];
    }

    const newRow: SampleTasks = {
      sampleId: entity.id,
      taskId: null,
      approximateTimeMinutes: null,
      applicablePpeWorn: false,
      taskName: null,
      taskGroupId: null,
      taskGroupName: null,
      startDate: null,
      endDate: null,
      taskNotes: null,
      calculatedTimeOnTask: null
    };

    entity.sampleTasks.push(newRow);
    entity.sampleTasks = [...entity.sampleTasks];

    return newRow;
  }

  /**
   * Remove a task row
   */
  removeTaskRow(index: number, entity: Sample, filteredTasks: FilteredTaskGroup[][], taskSearch: string[]): void {
    if (entity.sampleTasks) {
      entity.sampleTasks.splice(index, 1);
      entity.sampleTasks = [...entity.sampleTasks];
      filteredTasks.splice(index, 1);
      taskSearch.splice(index, 1);
    }
  }

  /**
   * Filter tasks based on search value
   */
  filterTasks(
    searchValue: string,
    rowIndex: number,
    tasks: (GroupLookup & { id: number; name: string })[],
    filteredTasks: FilteredTaskGroup[][]
  ): void {
    if (!searchValue) {
      filteredTasks[rowIndex] = [...tasks];
      return;
    }

    const lower = searchValue.toLowerCase();

    filteredTasks[rowIndex] = tasks
      .map(group => ({
        ...group,
        items: (group.items || []).filter(item =>
          item.name?.toLowerCase().includes(lower)
        )
      }))
      .filter(group => group.items?.length);
  }

  /**
   * Open task creation dialog
   */
  openTaskDialog(element?: SampleTasks): Observable<ITaskDialogData | undefined> {
    const taskDialogData: ITaskDialogData = {
      id: '',
      groupId: null,
    };
    const dialogRef = this.dialog.open(TaskDialogComponent, {
      width: '400px',
      disableClose: true,
      panelClass: 'no-gps-and-building',
      data: taskDialogData
    });
    return dialogRef.afterClosed();
  }

  /**
   * Drop rows added without picking a task (the API requires a task id on every row) and send the runtime as
   * whole minutes, which is all the API accepts.
   */
  prepareTasksForSave(sampleTasks?: SampleTasks[]): SampleTasks[] | undefined {
    if (!sampleTasks) return sampleTasks;
    return sampleTasks
      .filter(task => !!task.taskId)
      .map(task => task.approximateTimeMinutes == null
        ? task
        : { ...task, approximateTimeMinutes: Math.round(task.approximateTimeMinutes) });
  }

  /**
   * Validate for duplicate task IDs
   */
  validateNoDuplicateTasks(sampleTasks: SampleTasks[]): { hasDuplicates: boolean; duplicateIds: string[] } {
    if (!sampleTasks || !Array.isArray(sampleTasks)) {
      return { hasDuplicates: false, duplicateIds: [] };
    }

    const seen = new Set<string>();
    const duplicates: string[] = [];
    for (const task of sampleTasks) {
      if (task.taskId != null) {
        if (seen.has(task.taskId)) {
          duplicates.push(task.taskId);
        } else {
          seen.add(task.taskId);
        }
      }
    }

    return {
      hasDuplicates: duplicates.length > 0,
      duplicateIds: [...new Set(duplicates)]
    };
  }
}
